// @flow
import * as React from 'react';

import { Tooltip } from 'reshaped';
import type { FlyoutTriggerAttributes } from 'reshaped';

import type { ButtonProps } from '@gandi/reshaped';

type CustomButtonProps = {
  ...ButtonProps,
  attributes?: FlyoutTriggerAttributes,
};

type Props = {
  as: React.ComponentType<CustomButtonProps>,
  tooltipText?: React.Node,
  ...Omit<CustomButtonProps, 'as'>,
};

export function ButtonTooltip({
  as: Component,
  tooltipText,
  ...props
}: Props): React.MixedElement {
  const triggerRef = React.useRef<?HTMLElement>(null);
  const [width, setWidth] = React.useState(0);

  React.useEffect(() => {
    const element = triggerRef.current;
    let observer;

    if (element) {
      observer = new ResizeObserver(() => {
        setWidth(element.offsetWidth);
      });

      observer.observe(element);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [tooltipText]);

  return !tooltipText ? (
    <Component {...props} />
  ) : (
    <Tooltip
      text={tooltipText}
      position="top-start"
      contentMaxWidth={width ? `${width}px` : undefined}
    >
      {(attributes: FlyoutTriggerAttributes) => (
        <Component
          {...props}
          as="div"
          attributes={{
            ...attributes,
            ref: (node: HTMLDivElement) => {
              // ref tooltip (obligatoire)
              if (typeof attributes.ref === 'function') {
                attributes.ref(node);
              } else if (attributes.ref) {
                // eslint-disable-next-line no-param-reassign
                attributes.ref.current = node;
              }

              // ref interne pour mesurer
              triggerRef.current = node;
            },
          }}
        />
      )}
    </Tooltip>
  );
}
